// PrimeReact
import { Splitter, SplitterPanel } from "primereact/splitter";
import { useState } from "react";

// Toolkit
import {
  Drawer,
  DrawerContent,
  PanelWithDrawers,
  TopPanel,
} from "sj-ui-toolkit/src/atlas/components/PanelWithDrawers";
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import { MEDIA_QUERY_BELOW_LG, useMediaQuery } from "sj-ui-toolkit/src/hooks";

// Context
import { useTournamentContext } from "../../../context/TournamentContext";

// Sub-components
import Notes from "../Notes/Notes";
import ResourcesList from "../Resources/ResourcesList";
import LeftPanel from "./LeftPanel/LeftPanel";
import RightPanel from "./RightPanel/RightPanel";
import TournamentStart from "./TournamentStart";
import { VideoProvider, VideoLessonDetails } from "./VideoProvider";

// Styles
import S from "../_Lesson.module.scss";

// Main component
interface VideoLessonProps {
  lessonDetails: VideoLessonDetails;
  tournamentUuid?: string;
  onLessonFinish: () => void;
  exerciseOnly?: boolean;
}

const VideoLesson = ({
  lessonDetails,
  tournamentUuid,
  onLessonFinish,
  exerciseOnly,
}: VideoLessonProps) => {
  const { tournamentLessonNotStarted } = useTournamentContext();
  // Below lg, the Splitter's 30/70 side-by-side columns are too narrow (video, Monaco editor)
  // to use, so panels stack instead.
  const isStacked = useMediaQuery(MEDIA_QUERY_BELOW_LG);
  // Controlled so the drawer can be turned into a full-coverage overlay when stacked.
  const [notesOpen, setNotesOpen] = useState(false);

  const leftPanelContent = (
    <PanelWithDrawers className="max-h-full">
      <TopPanel className={C("pr-3", S.leftPanelScroll, isStacked && "pb-14")}>
        <LeftPanel lessonDetails={lessonDetails} />
      </TopPanel>
      {!exerciseOnly && (
        <Drawer
          isOpen={notesOpen}
          onOpenToggle={setNotesOpen}
          activeTab={0}
          // Stacked mode gives this section less height overall, so open the drawer wider within it.
          initialHeight={isStacked ? "85%" : "50%"}
          title="Notes"
          className={C(
            T.bgElevation1,
            "pr-3 rounded-t-sm",
            isStacked && (notesOpen ? S.notesOverlay : S.notesStickyBottom)
          )}
        >
          <DrawerContent title="Notes" className="h-full overflow-y-auto">
            <Notes lessonId={lessonDetails.id} />
          </DrawerContent>
          {lessonDetails.resources && (
            <DrawerContent title="Resources" className="h-full overflow-y-auto">
              <ResourcesList resources={lessonDetails.resources} />
            </DrawerContent>
          )}
        </Drawer>
      )}
    </PanelWithDrawers>
  );

  return (
    <VideoProvider
      lessonDetails={lessonDetails}
      tournamentUuid={tournamentUuid}
      onLessonFinish={onLessonFinish}
      exerciseOnly={exerciseOnly}
    >
      {tournamentUuid && tournamentLessonNotStarted && <TournamentStart />}
      {isStacked ? (
        // Video/quiz/exercise first since it's the primary content, lesson info + notes below.
        <div className="flex flex-col gap-2 h-full min-h-0">
          <div className="flex-[3] min-h-0 min-w-0 overflow-hidden">
            <RightPanel lessonDetails={lessonDetails} />
          </div>
          <div className={C("flex-[2] min-h-0 min-w-0 overflow-hidden flex flex-col", S.leftPanel)}>
            {leftPanelContent}
          </div>
        </div>
      ) : (
        <Splitter
          className={C(T.splitterGap, "max-h-full h-full")}
          aria-label="Resize lesson panels"
        >
          <SplitterPanel
            size={30}
            minSize={20}
            className={C("flex flex-col max-h-full", S.leftPanel)}
          >
            {leftPanelContent}
          </SplitterPanel>
          <SplitterPanel className="min-w-0 overflow-hidden h-full" size={70} minSize={40}>
            <RightPanel lessonDetails={lessonDetails} />
          </SplitterPanel>
        </Splitter>
      )}
    </VideoProvider>
  );
};

export default VideoLesson;
